// External libraries
import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { noop } from 'lodash';

// Zocdoc libraries
import { Metrics, MetricsClickable } from '@zocdoc/frontend-metrics';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { Link } from '@zocdoc-frontend-common/components-link';
import removeWhitespace from '@zocdoc-frontend-common/utils-stringutils/removeWhitespace';

// Link Farm
import { AppStoreLink, StyledGooglePlayLink } from './IconLinks';
import { SpecialtyLinks, InsuranceLinks } from './LinkConstants';

const LinkGroup = ({
    children,
    name,
    'data-title': dataTitle,
    'data-test': dataTest,
}) => (
    <section data-test={dataTest} data-title={dataTitle}>
        {name ? <StyledLinkGroupHeader>{name}</StyledLinkGroupHeader> : null}

        <StyledLinkList>
            {React.Children.map(children, (child, index) => (
                <StyledLinkWrapper key={index}>{child}</StyledLinkWrapper>
            ))}
        </StyledLinkList>
    </section>
);

const StyledLinkGroupHeader = styled.h3`
    color: ${p => p.theme.footer.seo.textColor};
    font: 14px/1.4 ${FontFamily.SharpSansBold};
    letter-spacing: 0.1px;
    padding-top: 2px;
    padding-bottom: 12px;
`;

const StyledLinkList = styled.ul`
    padding-bottom: 22px;
`;

const StyledLinkWrapper = styled.li`
    margin: 0 0 9px;
    font: 14px/1.2 ${FontFamily.SharpSansSemibold};
`;

const StyledLink = styled(Link)`
    border-bottom-color: ${p => p.theme.footer.seo.linkUnderlineColor};
    color: ${p => p.theme.footer.seo.linkColor};
    font: inherit;

    &:focus,
    &:hover {
        border-bottom-color: ${p => p.theme.footer.seo.linkUnderlineColorHover};
        color: ${p => p.theme.footer.seo.linkColorHover};
    }

    &:visited {
        border-bottom-color: ${p =>
            p.theme.footer.seo.linkUnderlineColorVisited};
        color: ${p => p.theme.footer.seo.linkColorVisited};
    }
`;

export class ZocdocLinkGroup extends React.Component {
    static propTypes = {
        hostOverride: PropTypes.string,
        onBeforeLinkClick: PropTypes.func,
    };

    static defaultProps = {
        hostOverride: '',
        onBeforeLinkClick: noop,
    };

    renderZdLink(message, href) {
        const { hostOverride, onBeforeLinkClick } = this.props;
        const shouldOverride = !href.startsWith('//');
        const host = shouldOverride ? hostOverride : '';
        return (
            <MetricsClickable
                action={`Click ${message}`}
                element={removeWhitespace(message)}
            >
                <StyledLink
                    beforeClick={onBeforeLinkClick}
                    data-title="Zocdoc"
                    href={`${host}${href}`}
                    target="_self"
                >
                    {message}
                </StyledLink>
            </MetricsClickable>
        );
    }

    render() {
        const { dataTest } = this.props;
        return (
            <Metrics component="ZocdocLinks">
                <LinkGroup name="Zocdoc" data-test={dataTest}>
                    {this.renderZdLink('Home', '/')}
                    {this.renderZdLink('About us', '/about/')}
                    {this.renderZdLink('Press', '/about/press/')}
                    {this.renderZdLink('Careers', '/about/careers/')}
                    {this.renderZdLink('Contact us', '/about/contactus/')}
                    {this.renderZdLink(
                        'Help',
                        '//support.zocdoc.com/patients/s/'
                    )}
                </LinkGroup>
            </Metrics>
        );
    }
}

export class DiscoverLinkGroup extends React.Component {
    static propTypes = {
        hostOverride: PropTypes.string,
        onBeforeLinkClick: PropTypes.func,
    };

    static defaultProps = {
        hostOverride: '',
        onBeforeLinkClick: noop,
    };

    renderDiscoverLink(text, linkBody, secondLineOfText) {
        const { hostOverride, onBeforeLinkClick } = this.props;
        const shouldOverrideHost = !linkBody.startsWith('http');
        return (
            <MetricsClickable
                action={`Click ${text}`}
                element={removeWhitespace(text)}
            >
                <StyledLink
                    beforeClick={onBeforeLinkClick}
                    data-title="Discover"
                    href={`${
                        shouldOverrideHost ? hostOverride : ''
                    }${linkBody}`}
                    target="_self"
                >
                    {text}
                    {secondLineOfText && <br />}
                    {secondLineOfText}
                </StyledLink>
            </MetricsClickable>
        );
    }

    render() {
        const { dataTest } = this.props;
        return (
            <Metrics component="DiscoverLinks">
                <LinkGroup data-test={dataTest} name={'Discover'}>
                    {this.renderDiscoverLink(
                        'The Paper Gown',
                        'https://thepapergown.zocdoc.com/',
                        'Stories for and about patients'
                    )}
                    {this.renderDiscoverLink(
                        'The Script',
                        'https://thescript.zocdoc.com/',
                        'Insights for doctors'
                    )}
                    {this.renderDiscoverLink(
                        'Community Standards',
                        '/about/communitystandards/'
                    )}
                    {this.renderDiscoverLink(
                        'Data and privacy',
                        '/about/topic/data-privacy'
                    )}
                    {this.renderDiscoverLink(
                        'Verified reviews',
                        '/about/verifiedreviews/'
                    )}
                </LinkGroup>
            </Metrics>
        );
    }
}

export class InsuranceLinkGroup extends React.Component {
    static propTypes = {
        hostOverride: PropTypes.string,
        onBeforeLinkClick: PropTypes.func,
    };

    static defaultProps = {
        hostOverride: '',
        onBeforeLinkClick: noop,
    };

    renderInsuranceLink(text, linkBody) {
        const { hostOverride, onBeforeLinkClick } = this.props;
        return (
            <MetricsClickable
                action={`Click ${text}`}
                element={removeWhitespace(text)}
                key={text}
            >
                <StyledLink
                    beforeClick={onBeforeLinkClick}
                    data-title="Insurance"
                    href={`${hostOverride}/${linkBody}`}
                    target="_self"
                >
                    {text}
                </StyledLink>
            </MetricsClickable>
        );
    }

    render() {
        const { dataTest } = this.props;
        return (
            <Metrics component="InsuranceLinks">
                <LinkGroup data-test={dataTest} name={'Insurance Carriers'}>
                    {InsuranceLinks.map(link =>
                        this.renderInsuranceLink(link.text, link.href)
                    )}
                </LinkGroup>
            </Metrics>
        );
    }
}

export class SpecialtiesLinkGroup extends React.Component {
    static propTypes = {
        hostOverride: PropTypes.string,
        onBeforeLinkClick: PropTypes.func,
    };

    static defaultProps = {
        hostOverride: '',
        onBeforeLinkClick: noop,
    };

    renderSpecialtyLink(message, linkBody) {
        const { hostOverride, onBeforeLinkClick } = this.props;

        return (
            <MetricsClickable
                action={`Click ${message}`}
                element={removeWhitespace(message)}
                key={message}
            >
                <StyledLink
                    beforeClick={onBeforeLinkClick}
                    data-test={`specialties-${message.toLowerCase()}-link`}
                    data-title="Specialties"
                    href={`${hostOverride}/${linkBody}`}
                    target="_self"
                >
                    {message}
                </StyledLink>
            </MetricsClickable>
        );
    }

    render() {
        const { dataTest } = this.props;
        return (
            <Metrics component="SpecialtiesLinks">
                <LinkGroup data-test={dataTest} name={'Top Specialties'}>
                    {SpecialtyLinks.map(link =>
                        this.renderSpecialtyLink(link.text, link.href)
                    )}
                </LinkGroup>
            </Metrics>
        );
    }
}

export function CTALinkGroup({
    onBeforeLinkClick,
    showPricingLink,
    hostOverride,
}) {
    return (
        <LinkGroup>
            <Metrics component="CtaLinks">
                <LinkGroup
                    name={'Are you a top doctor or health service?'}
                    data-test={'cta-link-group'}
                >
                    <MetricsClickable
                        action="Click List your practice on Zocdoc"
                        element="ListyourpracticeonZocdoc"
                    >
                        <StyledLink
                            beforeClick={onBeforeLinkClick}
                            data-test="AreYouADoctorOrDentist"
                            data-title="AreYouADoctorOrDentist"
                            href={`${hostOverride}/about/join?utm_medium=organicpro&utm_source=website&utm_campaign=PatientSite_footer`}
                            target="_self"
                        >
                            {`List your practice on Zocdoc`}
                        </StyledLink>
                    </MetricsClickable>

                    <PricingLink
                        hostOverride={hostOverride}
                        onBeforeLinkClick={onBeforeLinkClick}
                        showPricingLink={showPricingLink}
                    />

                    <MetricsClickable
                        action="Click Become an API partner"
                        element="BecomeanAPIpartner"
                    >
                        <StyledLink
                            beforeClick={onBeforeLinkClick}
                            data-test="BecomeanAPIpartner"
                            data-title="BecomeanAPIpartner"
                            href={`${hostOverride}/about/integrate/`}
                            target="_self"
                        >
                            {`Become an API partner`}
                        </StyledLink>
                    </MetricsClickable>
                </LinkGroup>
            </Metrics>

            <Metrics component="BadgeLinks">
                <LinkGroup
                    name="Get the Zocdoc app"
                    data-title="Badges"
                    data-test="badges-link-group"
                >
                    <MetricsClickable
                        action="Click App Store"
                        element="AppStore"
                    >
                        <AppStoreLink
                            height={36}
                            onBeforeLinkClick={onBeforeLinkClick}
                            title="Download the Zocdoc app from the Apple App Store"
                            data-test={'app-store-icon'}
                        />
                    </MetricsClickable>

                    <MetricsClickable
                        action="Click Google Play"
                        element="GooglePlay"
                    >
                        <StyledGooglePlayLink
                            height={36}
                            onBeforeLinkClick={onBeforeLinkClick}
                            title="Download the Zocdoc app from the Google Play Store"
                            data-test={'google-play-icon'}
                        />
                    </MetricsClickable>
                </LinkGroup>
            </Metrics>
        </LinkGroup>
    );
}

function PricingLink({ hostOverride, onBeforeLinkClick, showPricingLink }) {
    return showPricingLink ? (
        <Metrics element="LearnaboutZocdocforHealthSystems">
            <MetricsClickable action="Click Learn about Zocdoc for Health Systems">
                <StyledLink
                    beforeClick={onBeforeLinkClick}
                    data-test="ZocdocPricing"
                    data-title="ZocdocPricing"
                    href={`${hostOverride}/about/newpricing/`}
                    target="_self"
                >
                    {`Learn about Zocdoc for Health Systems`}
                </StyledLink>
            </MetricsClickable>
        </Metrics>
    ) : null;
}
